import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import type { ChartCardChartContainerProps, ChartCardPerformanceContainerProps, ChartCardTableContainerProps, MetricsMap } from '../internalTypes';
import type { ChartDataRow } from '../types';
import { aggregateAndTransposeData, applyFilters, fillMissingDateRangeData, filterDataIfFirstAxisChoiceValuesOrValuesOf, inconsistentProcessedVsOriginalData, normaliseNullDimensionValuesForChart, orderTransposedMetricsLists, processDataForCalculatedMetrics, sortDataIfFirstAxisChoiceValuesOrValuesOf, untransposeData } from '../utils/data';
import { errorLocal, toNullDimensionOptionValue } from '../utils/generics';
import { expandMetricsFromFormulas, generateGroupByAndMetricsFromChartDefinition, generateGroupByAndMetricsFromColumns, getChartDimensionAndChoice } from '../utils/choices';
import { useDeepCompareMemoize } from './generics';

/**
 * usePrepareData is a hook that prepares the data for the chart card chart or table container.
 *
 * @param id - The id of the card.
 * @param chart - The chart definition.
 * @param columns - The table columns definition.
 * @param data - The raw data to prepare.
 * @param dataCompare - The raw compare data to prepare.
 * @param dimensionsMap - The dimensions map.
 * @param metricsMap - The metrics map.
 * @param state - The state of the card.
 * @returns The prepared data.
 */
const usePrepareData = props => {
  const $ = _c(91);
  const {
    id: cardId,
    chart,
    columns,
    data,
    dataCompare,
    dimensionsMap,
    metricsMap,
    state: t0
  } = props;
  const {
    filters,
    choicesMap,
    selections,
    dateRange,
    zoomedIn,
    firstAxisDomainDataKey,
    firstAxisDomain
  } = t0;
  const isServerFetch = "isServerFetch" in props && props.isServerFetch;
  const dimensionsValuesMap = "dimensionsValuesMap" in props ? props.dimensionsValuesMap : undefined;
  let t1;
  if ($[0] !== chart || $[1] !== choicesMap || $[2] !== columns || $[3] !== data || $[4] !== dataCompare || $[5] !== dimensionsMap || $[6] !== metricsMap) {
    t1 = untransposeData({
      data,
      dataCompare,
      chart,
      columns,
      choicesMap,
      dimensionsMap,
      metricsMap
    });
    $[0] = chart;
    $[1] = choicesMap;
    $[2] = columns;
    $[3] = data;
    $[4] = dataCompare;
    $[5] = dimensionsMap;
    $[6] = metricsMap;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const {
    untransposedData,
    untransposedDataCompare,
    newMetricsMap: untransposedMetricsMap
  } = t1;
  if (inconsistentProcessedVsOriginalData(untransposedData, data) || inconsistentProcessedVsOriginalData(untransposedDataCompare, dataCompare)) {
    errorLocal("usePrepareData untransposedData is empty", {
      untransposedData,
      untransposedDataCompare,
      data,
      dataCompare,
      filters
    });
  }
  let t2;
  if ($[8] !== dimensionsMap || $[9] !== filters || $[10] !== untransposedData || $[11] !== untransposedDataCompare) {
    t2 = applyFilters({
      data: untransposedData,
      dataCompare: untransposedDataCompare,
      dimensionsMap,
      filters
    });
    $[8] = dimensionsMap;
    $[9] = filters;
    $[10] = untransposedData;
    $[11] = untransposedDataCompare;
    $[12] = t2;
  } else {
    t2 = $[12];
  }
  const {
    filteredData: filteredRawData,
    filteredDataCompare: filteredRawDataCompare
  } = t2;
  if (inconsistentProcessedVsOriginalData(filteredRawData, untransposedData) || inconsistentProcessedVsOriginalData(filteredRawDataCompare, untransposedDataCompare)) {
    errorLocal("usePrepareData filteredRawData is empty", {
      filteredRawData,
      untransposedData,
      filteredRawDataCompare,
      untransposedDataCompare,
      filters
    });
  }
  let groupBy;
  let metrics;
  let transposeMetrics;
  if ($[13] !== chart || $[14] !== choicesMap || $[15] !== columns || $[16] !== dimensionsMap || $[17] !== metricsMap || $[18] !== selections) {
    ({
      groupBy,
      metrics,
      transposeMetrics
    } = chart == null ? generateGroupByAndMetricsFromColumns({
      columns,
      dimensionsMap,
      metricsMap,
      selections
    }) : generateGroupByAndMetricsFromChartDefinition({
      chart,
      choicesMap,
      dimensionsMap,
      selections
    }));
    expandMetricsFromFormulas(metrics, metricsMap);
    $[13] = chart;
    $[14] = choicesMap;
    $[15] = columns;
    $[16] = dimensionsMap;
    $[17] = metricsMap;
    $[18] = selections;
    $[19] = groupBy;
    $[20] = metrics;
    $[21] = transposeMetrics;
  } else {
    groupBy = $[19];
    metrics = $[20];
    transposeMetrics = $[21];
  }
  let t3;
  if ($[22] !== groupBy || $[23] !== metrics || $[24] !== transposeMetrics) {
    t3 = {
      groupBy,
      metrics,
      transposeMetrics
    };
    $[22] = groupBy;
    $[23] = metrics;
    $[24] = transposeMetrics;
    $[25] = t3;
  } else {
    t3 = $[25];
  }
  const groupByAndMetrics = t3;
  const {
    groupBy: groupBy_0,
    metrics: metrics_0,
    transposeMetrics: transposeMetrics_0
  } = useDeepCompareMemoize(groupByAndMetrics);
  let t4;
  if ($[26] !== dimensionsMap || $[27] !== filteredRawData || $[28] !== filteredRawDataCompare || $[29] !== groupBy_0 || $[30] !== isServerFetch || $[31] !== metrics_0 || $[32] !== transposeMetrics_0 || $[33] !== untransposedMetricsMap) {
    bb0: {
      const transposedMetricsLists = new Map();
      if (isServerFetch) {
        const aggregatedAndTransposedData = processDataForCalculatedMetrics({
          data: filteredRawData,
          metricsMap: untransposedMetricsMap
        });
        const aggregatedAndTransposedDataCompare = filteredRawDataCompare == null ? undefined : processDataForCalculatedMetrics({
          data: filteredRawDataCompare,
          metricsMap: untransposedMetricsMap
        });
        t4 = {
          aggregatedAndTransposedData,
          aggregatedAndTransposedDataCompare,
          newMetricsMap: untransposedMetricsMap,
          transposedMetricsLists
        };
        break bb0;
      }
      const {
        aggregatedAndTransposedData: aggregatedAndTransposedData_0,
        aggregatedAndTransposedDataCompare: aggregatedAndTransposedDataCompare_0,
        newMetricsMap
      } = aggregateAndTransposeData({
        data: filteredRawData,
        dataCompare: filteredRawDataCompare,
        groupBy: groupBy_0,
        metrics: metrics_0,
        metricsMap: untransposedMetricsMap,
        dimensionsMap,
        transposedMetricsLists,
        transposeMetrics: transposeMetrics_0
      });
      if (inconsistentProcessedVsOriginalData(aggregatedAndTransposedData_0, filteredRawData) || inconsistentProcessedVsOriginalData(aggregatedAndTransposedDataCompare_0, filteredRawDataCompare)) {
        errorLocal("usePrepareData aggregatedAndTransposedData is empty", {
          aggregatedAndTransposedData: aggregatedAndTransposedData_0,
          filteredRawData,
          aggregatedAndTransposedDataCompare: aggregatedAndTransposedDataCompare_0,
          filteredRawDataCompare,
          groupBy: groupBy_0,
          metrics: metrics_0,
          transposeMetrics: transposeMetrics_0,
          transposedMetricsLists
        });
      }
      const orderedTranspoedMetricsLists = orderTransposedMetricsLists(transposedMetricsLists, transposeMetrics_0, aggregatedAndTransposedData_0);
      let t5;
      if ($[35] !== aggregatedAndTransposedDataCompare_0 || $[36] !== aggregatedAndTransposedData_0 || $[37] !== newMetricsMap || $[38] !== orderedTranspoedMetricsLists) {
        t5 = {
          aggregatedAndTransposedData: aggregatedAndTransposedData_0,
          aggregatedAndTransposedDataCompare: aggregatedAndTransposedDataCompare_0,
          newMetricsMap,
          transposedMetricsLists: orderedTranspoedMetricsLists
        };
        $[35] = aggregatedAndTransposedDataCompare_0;
        $[36] = aggregatedAndTransposedData_0;
        $[37] = newMetricsMap;
        $[38] = orderedTranspoedMetricsLists;
        $[39] = t5;
      } else {
        t5 = $[39];
      }
      t4 = t5;
    }
    $[26] = dimensionsMap;
    $[27] = filteredRawData;
    $[28] = filteredRawDataCompare;
    $[29] = groupBy_0;
    $[30] = isServerFetch;
    $[31] = metrics_0;
    $[32] = transposeMetrics_0;
    $[33] = untransposedMetricsMap;
    $[34] = t4;
  } else {
    t4 = $[34];
  }
  const {
    aggregatedAndTransposedData: aggregatedAndTransposedData_1,
    aggregatedAndTransposedDataCompare: aggregatedAndTransposedDataCompare_1,
    newMetricsMap: newMetricsMap_0,
    transposedMetricsLists: transposedMetricsLists_0
  } = t4;
  let t5;
  if ($[40] !== chart || $[41] !== choicesMap || $[42] !== dimensionsMap || $[43] !== selections) {
    t5 = getChartDimensionAndChoice({
      chart,
      choicesMap,
      dimensionsMap,
      selections
    });
    $[40] = chart;
    $[41] = choicesMap;
    $[42] = dimensionsMap;
    $[43] = selections;
    $[44] = t5;
  } else {
    t5 = $[44];
  }
  const {
    dimension: axisDimension,
    choice: axisChoice
  } = t5;
  let t6;
  if ($[45] !== aggregatedAndTransposedDataCompare_1 || $[46] !== aggregatedAndTransposedData_1 || $[47] !== axisChoice || $[48] !== axisDimension || $[49] !== cardId || $[50] !== chart || $[51] !== dateRange || $[52] !== metrics_0) {
    bb1: {
      const filteredData = chart == null ? aggregatedAndTransposedData_1 : filterDataIfFirstAxisChoiceValuesOrValuesOf({
        cardId,
        chart,
        metrics: metrics_0,
        axisDimension,
        axisChoice,
        data: aggregatedAndTransposedData_1
      });
      const filteredDataCompare = chart == null ? aggregatedAndTransposedDataCompare_1 : filterDataIfFirstAxisChoiceValuesOrValuesOf({
        cardId,
        chart,
        metrics: metrics_0,
        axisDimension,
        axisChoice,
        data: aggregatedAndTransposedDataCompare_1
      });
      const filledDateRangeData = fillMissingDateRangeData({
        axisDimension,
        data: filteredData,
        dateRange
      });
      const filledDateRangeDataCompare = fillMissingDateRangeData({
        axisDimension,
        data: filteredDataCompare,
        dateRange
      });
      if (chart == null) {
        t6 = {
          sortedData: filledDateRangeData,
          sortedDataCompare: filledDateRangeDataCompare
        };
        break bb1;
      }
      const sortedData = sortDataIfFirstAxisChoiceValuesOrValuesOf({
        cardId,
        chart,
        metrics: metrics_0,
        axisDimension,
        axisChoice,
        data: filledDateRangeData
      });
      const sortedDataCompare = sortDataIfFirstAxisChoiceValuesOrValuesOf({
        cardId,
        chart,
        metrics: metrics_0,
        axisDimension,
        axisChoice,
        data: filledDateRangeDataCompare
      });
      let t7;
      if ($[54] !== sortedData || $[55] !== sortedDataCompare) {
        t7 = {
          sortedData,
          sortedDataCompare
        };
        $[54] = sortedData;
        $[55] = sortedDataCompare;
        $[56] = t7;
      } else {
        t7 = $[56];
      }
      t6 = t7;
    }
    $[45] = aggregatedAndTransposedDataCompare_1;
    $[46] = aggregatedAndTransposedData_1;
    $[47] = axisChoice;
    $[48] = axisDimension;
    $[49] = cardId;
    $[50] = chart;
    $[51] = dateRange;
    $[52] = metrics_0;
    $[53] = t6;
  } else {
    t6 = $[53];
  }
  const {
    sortedData: filteredAndSortedData,
    sortedDataCompare: filteredAndSortedDataCompare
  } = t6;
  if (inconsistentProcessedVsOriginalData(filteredAndSortedData, aggregatedAndTransposedData_1) || inconsistentProcessedVsOriginalData(filteredAndSortedDataCompare, aggregatedAndTransposedDataCompare_1)) {
    errorLocal("usePrepareData filteredAndSortedData is empty", {
      aggregatedAndTransposedData: aggregatedAndTransposedData_1,
      filteredAndSortedData,
      filteredAndSortedDataCompare,
      aggregatedAndTransposedDataCompare: aggregatedAndTransposedDataCompare_1,
      filters,
      groupBy: groupBy_0,
      metrics: metrics_0,
      transposeMetrics: transposeMetrics_0
    });
  }
  let t7;
  bb2: {
    if ((zoomedIn === true || zoomedIn === "first") && firstAxisDomainDataKey != null && typeof firstAxisDomain?.[0] === "string") {
      let t8;
      if ($[57] !== filteredAndSortedData || $[58] !== firstAxisDomain || $[59] !== firstAxisDomainDataKey) {
        let t9;
        if ($[61] !== firstAxisDomain || $[62] !== firstAxisDomainDataKey) {
          t9 = row => {
            const rowValue = toNullDimensionOptionValue(row[firstAxisDomainDataKey]);
            return (firstAxisDomain as string[]).includes(rowValue);
          };
          $[61] = firstAxisDomain;
          $[62] = firstAxisDomainDataKey;
          $[63] = t9;
        } else {
          t9 = $[63];
        }
        t8 = filteredAndSortedData.filter(t9);
        $[57] = filteredAndSortedData;
        $[58] = firstAxisDomain;
        $[59] = firstAxisDomainDataKey;
        $[60] = t8;
      } else {
        t8 = $[60];
      }
      const zoomedInData = t8;
      let t9;
      if ($[64] !== filteredAndSortedDataCompare || $[65] !== firstAxisDomain || $[66] !== firstAxisDomainDataKey) {
        t9 = filteredAndSortedDataCompare?.filter(row_0 => {
          const rowValue_0 = toNullDimensionOptionValue(row_0[firstAxisDomainDataKey]);
          return (firstAxisDomain as string[]).includes(rowValue_0);
        });
        $[64] = filteredAndSortedDataCompare;
        $[65] = firstAxisDomain;
        $[66] = firstAxisDomainDataKey;
        $[67] = t9;
      } else {
        t9 = $[67];
      }
      const zoomedInDataCompare = t9;
      let t10;
      if ($[68] !== zoomedInData || $[69] !== zoomedInDataCompare) {
        t10 = {
          zoomedInData,
          zoomedInDataCompare
        };
        $[68] = zoomedInData;
        $[69] = zoomedInDataCompare;
        $[70] = t10;
      } else {
        t10 = $[70];
      }
      t7 = t10;
      break bb2;
    }
    let t8;
    if ($[71] !== filteredAndSortedData || $[72] !== filteredAndSortedDataCompare) {
      t8 = {
        zoomedInData: filteredAndSortedData,
        zoomedInDataCompare: filteredAndSortedDataCompare
      };
      $[71] = filteredAndSortedData;
      $[72] = filteredAndSortedDataCompare;
      $[73] = t8;
    } else {
      t8 = $[73];
    }
    t7 = t8;
  }
  const {
    zoomedInData: zoomedInData_0,
    zoomedInDataCompare: zoomedInDataCompare_0
  } = t7;
  let t8;
  bb3: {
    if (chart == null) {
      let t9;
      if ($[74] !== zoomedInDataCompare_0 || $[75] !== zoomedInData_0) {
        t9 = {
          chartData: zoomedInData_0,
          chartDataCompare: zoomedInDataCompare_0
        };
        $[74] = zoomedInDataCompare_0;
        $[75] = zoomedInData_0;
        $[76] = t9;
      } else {
        t9 = $[76];
      }
      t8 = t9;
      break bb3;
    }
    let t9;
    if ($[77] !== dimensionsValuesMap || $[78] !== zoomedInData_0) {
      t9 = normaliseNullDimensionValuesForChart({
        data: zoomedInData_0,
        dimensionsValuesMap
      });
      $[77] = dimensionsValuesMap;
      $[78] = zoomedInData_0;
      $[79] = t9;
    } else {
      t9 = $[79];
    }
    let t10;
    if ($[80] !== dimensionsValuesMap || $[81] !== zoomedInDataCompare_0) {
      t10 = zoomedInDataCompare_0 == null ? undefined : normaliseNullDimensionValuesForChart({
        data: zoomedInDataCompare_0,
        dimensionsValuesMap
      });
      $[80] = dimensionsValuesMap;
      $[81] = zoomedInDataCompare_0;
      $[82] = t10;
    } else {
      t10 = $[82];
    }
    let t11;
    if ($[83] !== t10 || $[84] !== t9) {
      t11 = {
        chartData: t9,
        chartDataCompare: t10
      };
      $[83] = t10;
      $[84] = t9;
      $[85] = t11;
    } else {
      t11 = $[85];
    }
    t8 = t11;
  }
  const {
    chartData,
    chartDataCompare
  } = t8;
  let t9;
  if ($[86] !== chartData || $[87] !== chartDataCompare || $[88] !== newMetricsMap_0 || $[89] !== transposedMetricsLists_0) {
    t9 = {
      processedData: chartData,
      processedDataCompare: chartDataCompare,
      newMetricsMap: newMetricsMap_0,
      transposedMetricsLists: transposedMetricsLists_0
    };
    $[86] = chartData;
    $[87] = chartDataCompare;
    $[88] = newMetricsMap_0;
    $[89] = transposedMetricsLists_0;
    $[90] = t9;
  } else {
    t9 = $[90];
  }
  return t9;
};
export default usePrepareData;